import type { StorageResponsePayload } from "@swiggy-private/native-bridge";
import { STORAGE_TYPES } from "@swiggy-private/native-bridge";

export enum WebStorageLocation {
    LOCAL = "LOCAL",
    SESSION = "SESSION",
}

const WINDOW_NOT_AVAILABLE = "Window not available";
const BAD_PAYLOAD_FOR_STORAGE = "JS Bridge: Bad Payload given to Storage.";

// Web storage helpers
export const storeItemsToLocalStorage = (data: Record<string, string>) => {
    Object.entries(data).forEach(([key, value]) => {
        localStorage.setItem(key, value);
    });
    return {};
};

export const removeItemsFromLocalStorage = (data: Record<string, string>) => {
    Object.entries(data).forEach(([key]) => {
        localStorage.removeItem(key);
    });
    return {};
};

export const getItemsFromLocalStorage = (data: Record<string, string>): StorageResponsePayload => {
    const output: { [key: string]: string } = {};
    Object.entries(data).forEach(([key]) => {
        const result = localStorage.getItem(key);
        if (result !== null) {
            output[key] = result;
        }
    });
    return { data: output };
};

export const storeItemsToSessionStorage = (data: Record<string, string>) => {
    Object.entries(data).forEach(([key, value]) => {
        sessionStorage.setItem(key, value);
    });
    return {};
};

export const removeItemsFromSessionStorage = (data: Record<string, string>) => {
    Object.entries(data).forEach(([key]) => {
        sessionStorage.removeItem(key);
    });
    return {};
};

export const getItemsFromSessionStorage = (
    data: Record<string, string>
): StorageResponsePayload => {
    const output: { [key: string]: string } = {};
    Object.entries(data).forEach(([key]) => {
        const result = sessionStorage.getItem(key);
        if (result !== null) {
            output[key] = result;
        }
    });
    return { data: output };
};

export const webStorage = (
    payload: { type: string; data: Record<string, string> },
    storageType: WebStorageLocation = WebStorageLocation.LOCAL
): Promise<StorageResponsePayload | {}> => {
    if (typeof window === "undefined") {
        return Promise.reject({
            statusMessage: WINDOW_NOT_AVAILABLE,
        });
    }

    if (!payload || !payload.type || !payload.data) {
        return Promise.reject({
            statusMessage: BAD_PAYLOAD_FOR_STORAGE,
        });
    }

    const storageMethodsMap = {
        [WebStorageLocation.LOCAL]: {
            save: storeItemsToLocalStorage,
            get: getItemsFromLocalStorage,
            remove: removeItemsFromLocalStorage,
        },
        [WebStorageLocation.SESSION]: {
            save: storeItemsToSessionStorage,
            get: getItemsFromSessionStorage,
            remove: removeItemsFromSessionStorage,
        },
    };

    const storageMethods = storageMethodsMap[storageType];
    if (!storageMethods) {
        return Promise.resolve({});
    }

    switch (payload.type) {
        case STORAGE_TYPES.SAVE:
            return Promise.resolve(storageMethods.save(payload.data));
        case STORAGE_TYPES.GET:
            return Promise.resolve(storageMethods.get(payload.data));
        case STORAGE_TYPES.DELETE:
            return Promise.resolve(storageMethods.remove(payload.data));
        default:
            return Promise.resolve({});
    }
};
